import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCart, getServices, getHeaderNavigation } from '../firebase/config';
import { getDatabase, ref, get } from 'firebase/database';
import './Header.css';
import logo from '../img/logo.png';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [services, setServices] = useState({});
  const [customServices, setCustomServices] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headerLoading, setHeaderLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  const dropdownRefs = useRef({});
  const timeoutRef = useRef(null);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for cart update events
  useEffect(() => {
    const handleCartUpdate = (event) => {
      const { userId, isLoggedIn, cartData } = event.detail;
      
      if (cartData && Array.isArray(cartData)) {
        setCartCount(cartData.length);
      } else {
        const user = localStorage.getItem('currentUser');
        if (user) {
          try {
            const parsedUser = JSON.parse(user);
            if (parsedUser && parsedUser.isLoggedIn && parsedUser.userId) {
              loadCartCount(parsedUser.userId);
            }
          } catch (e) {}
        } else {
          loadCartCount(null);
        }
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('cart_')) {
        loadCartCount(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const checkUser = () => {
      const user = localStorage.getItem('currentUser');
      if (user) {
        try {
          const parsedUser = JSON.parse(user);
          if (parsedUser && parsedUser.isLoggedIn === true && parsedUser.userId) {
            setCurrentUser(parsedUser);
            loadCartCount(parsedUser.userId);
          } else {
            setCurrentUser(null);
            loadCartCount(null);
            if (parsedUser && !parsedUser.isLoggedIn) {
              localStorage.removeItem('currentUser');
            }
          }
        } catch (e) {
          console.error('Error parsing user:', e);
          setCurrentUser(null);
          loadCartCount(null);
          localStorage.removeItem('currentUser');
        }
      } else {
        setCurrentUser(null);
        loadCartCount(null);
      }
    };

    checkUser();

    const handleStorageChange = (e) => {
      if (e.key === 'currentUser') {
        checkUser();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Build menu from services with custom services included
  const buildMenuFromServices = (allServices) => {
    // Group services by category
    const groupedByCategory = {};
    Object.entries(allServices).forEach(([id, service]) => {
      const category = service.category || 'Uncategorized';
      if (!groupedByCategory[category]) {
        groupedByCategory[category] = [];
      }
      groupedByCategory[category].push({
        id: id,
        label: service.name,
        path: `/service/${id}`,
        isCustom: service.isCustom || false
      });
    });

    // Build children array
    const children = [];
    Object.entries(groupedByCategory).forEach(([category, items]) => {
      children.push({
        id: `heading-${category.replace(/\s+/g, '-')}`,
        type: 'heading',
        label: category
      });
      items.forEach(item => {
        children.push({
          id: item.id,
          type: 'link',
          label: item.label,
          path: item.path,
          isCustom: item.isCustom || false
        });
      });
    });

    return [
      {
        id: 'home',
        label: 'Home',
        path: '/',
        type: 'link'
      },
      {
        id: 'about',
        label: 'About Us',
        path: '/about',
        type: 'link'
      },
      {
        id: 'career-services',
        label: 'Career Services',
        type: 'dropdown',
        children: children
      }
    ];
  };

  // Merge custom services into Firebase navigation
  const mergeCustomServicesIntoNav = (firebaseNav, customServicesData) => {
    if (!firebaseNav || firebaseNav.length === 0) return firebaseNav;
    
    // Find the career-services dropdown
    const careerServicesItem = firebaseNav.find(item => item.id === 'career-services' || item.id === 'services');
    
    if (!careerServicesItem) {
      // If no career-services found, add it
      const allServices = { ...services };
      customServicesData.forEach(service => {
        if (service.id) {
          allServices[service.id] = {
            ...service,
            isCustom: true
          };
        }
      });
      const newNav = buildMenuFromServices(allServices);
      return newNav;
    }
    
    // If career-services exists, merge custom services into its children
    const children = careerServicesItem.children || [];
    
    // Get existing categories from children
    const existingCategories = {};
    let currentHeading = null;
    children.forEach(child => {
      if (child.type === 'heading') {
        currentHeading = child.label.replace('▶', '').trim();
        existingCategories[currentHeading] = [];
      } else if (currentHeading && child.type !== 'heading' && child.type !== 'divider') {
        if (existingCategories[currentHeading]) {
          existingCategories[currentHeading].push(child);
        }
      }
    });
    
    // Add custom services to their categories
    customServicesData.forEach(service => {
      if (!service.id) return;
      
      const category = service.category || 'Uncategorized';
      if (!existingCategories[category]) {
        existingCategories[category] = [];
      }
      
      // Check if service already exists
      const exists = existingCategories[category].some(item => item.id === service.id);
      if (!exists) {
        existingCategories[category].push({
          id: service.id,
          type: 'link',
          label: service.name,
          path: `/service/${service.id}`,
          isCustom: true
        });
      }
    });
    
    // Rebuild children array
    const newChildren = [];
    Object.entries(existingCategories).forEach(([category, items]) => {
      newChildren.push({
        id: `heading-${category.replace(/\s+/g, '-')}`,
        type: 'heading',
        label: category
      });
      items.forEach(item => {
        newChildren.push({
          id: item.id,
          type: item.type || 'link',
          label: item.label,
          path: item.path,
          isCustom: item.isCustom || false
        });
      });
    });
    
    // Update the career-services item
    const updatedNav = firebaseNav.map(item => {
      if (item.id === 'career-services' || item.id === 'services') {
        return {
          ...item,
          children: newChildren
        };
      }
      return item;
    });
    
    return updatedNav;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesResult = await getServices();
        let allServices = {};
        let customServicesData = [];
        
        if (servicesResult.success) {
          setServices(servicesResult.data);
          allServices = { ...servicesResult.data };
        }
        
        // Fetch custom services from customService node
        const db = getDatabase();
        const customSnapshot = await get(ref(db, 'customService'));
        
        if (customSnapshot.exists()) {
          const data = customSnapshot.val();
          customServicesData = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
            isCustom: true
          }));
          setCustomServices(customServicesData);
          
          // Add custom services to all services
          customServicesData.forEach(service => {
            if (service.id) {
              allServices[service.id] = {
                ...service,
                isCustom: true
              };
            }
          });
        }
        
        setLoading(false);

        // Try to get header navigation from Firebase
        const navResult = await getHeaderNavigation();
        let finalNavItems = [];
        
        if (navResult.success && navResult.data && navResult.data.items && navResult.data.items.length > 0) {
          // Use Firebase navigation but merge custom services
          finalNavItems = mergeCustomServicesIntoNav(navResult.data.items, customServicesData);
          setNavItems(finalNavItems);
        } else {
          // Fallback: Build navigation from services
          const navItemsFromServices = buildMenuFromServices(allServices);
          setNavItems(navItemsFromServices);
        }
        setHeaderLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
        setHeaderLoading(false);
      }
    };

    fetchData();
  }, []);

  const loadCartCount = async (userId) => {
    try {
      let count = 0;
      if (userId) {
        const result = await getCart(userId);
        if (result.success && result.data) {
          count = result.data.length;
        }
      } else {
        const savedCart = localStorage.getItem('cartItems');
        if (savedCart) {
          try {
            const items = JSON.parse(savedCart);
            count = items.length;
          } catch (e) {}
        }
        const guestId = localStorage.getItem('guestUserId');
        if (guestId) {
          const guestCart = localStorage.getItem(`cart_${guestId}`);
          if (guestCart) {
            try {
              const items = JSON.parse(guestCart);
              if (items.length > count) count = items.length;
            } catch (e) {}
          }
        }
      }
      setCartCount(count);
    } catch (error) {
      console.error('Error loading cart count:', error);
      setCartCount(0);
    }
  };

  const handleNavigation = (path) => {
    setMenuOpen(false);
    setHoveredDropdown(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    navigate(path);
  };

  const handleMouseEnter = (name) => {
    if (isMobile) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setHoveredDropdown(name);
  };

  const handleMouseLeave = (name) => {
    if (isMobile) return;
    
    timeoutRef.current = setTimeout(() => {
      const dropdownElement = dropdownRefs.current[name];
      if (dropdownElement && dropdownElement.matches(':hover')) {
        return;
      }
      setHoveredDropdown(null);
    }, 150);
  };

  const toggleDropdown = (name, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setHoveredDropdown(hoveredDropdown === name ? null : name);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setCartCount(0);
    navigate('/');
    window.location.reload();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (hoveredDropdown && !e.target.closest('.nav-item.dropdown')) {
        setHoveredDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [hoveredDropdown]);

  const getCategoryIcon = (category) => {
    const icons = {
      'Career Blaze': 'fa-fire',
      'Improve Profile': 'fa-user-edit',
      'Profile Verification': 'fa-check-circle',
      'Career Assistance': 'fa-hand-holding-heart',
      'Maximize Reach': 'fa-rocket',
      'Profile ID': 'fa-id-card'
    };
    return icons[category] || 'fa-cog';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Career Blaze': '#FF6B6B',
      'Improve Profile': '#4ECDC4',
      'Profile Verification': '#45B7D1',
      'Career Assistance': '#96CEB4',
      'Maximize Reach': '#FFEAA7',
      'Profile ID': '#DDA0DD'
    };
    return colors[category] || '#65d2bc';
  };

  // Render mega menu with custom services
  const renderCareerServicesMegaMenu = (children) => {
    // Combine regular and custom services
    const allServices = { ...services };
    
    customServices.forEach((service) => {
      if (service.id) {
        allServices[service.id] = {
          ...service,
          isCustom: true
        };
      }
    });

    // If no children provided or empty, build from all services
    let menuChildren = children;
    if (!menuChildren || menuChildren.length === 0) {
      // Group services by category
      const groupedItems = {};
      Object.entries(allServices).forEach(([id, service]) => {
        const category = service.category || 'Uncategorized';
        if (!groupedItems[category]) {
          groupedItems[category] = [];
        }
        groupedItems[category].push({
          id: id,
          label: service.name,
          path: `/service/${id}`,
          isCustom: service.isCustom || false
        });
      });

      // Build children array
      const newChildren = [];
      Object.entries(groupedItems).forEach(([category, items]) => {
        newChildren.push({
          id: `heading-${category.replace(/\s+/g, '-')}`,
          type: 'heading',
          label: category
        });
        items.forEach(item => {
          newChildren.push({
            id: item.id,
            type: 'link',
            label: item.label,
            path: item.path,
            isCustom: item.isCustom || false
          });
        });
      });
      menuChildren = newChildren;
    }

    // Group items by heading
    const groupedItems = {};
    let currentHeading = null;
    
    menuChildren.forEach(child => {
      if (child.type === 'heading') {
        currentHeading = child.label.replace('▶', '').trim();
        groupedItems[currentHeading] = [];
      } else if (child.type === 'divider') {
        // Skip dividers
      } else if (currentHeading && child.type !== 'heading' && child.type !== 'divider') {
        if (groupedItems[currentHeading]) {
          groupedItems[currentHeading].push(child);
        }
      }
    });

    const headings = Object.keys(groupedItems);

    // If no headings found, try to build from all services directly
    if (headings.length === 0) {
      // Group all services by category
      const directGrouped = {};
      Object.entries(allServices).forEach(([id, service]) => {
        const category = service.category || 'Uncategorized';
        if (!directGrouped[category]) {
          directGrouped[category] = [];
        }
        directGrouped[category].push({
          id: id,
          label: service.name,
          path: `/service/${id}`,
          isCustom: service.isCustom || false
        });
      });

      return React.createElement(
        'div',
        { 
          className: `mega-menu ${hoveredDropdown === 'career-services' ? 'show' : ''}`,
          ref: (el) => dropdownRefs.current['career-services'] = el
        },
        React.createElement(
          'div',
          { className: 'mega-grid' },
          Object.entries(directGrouped).map(([category, items]) => {
            const icon = getCategoryIcon(category);
            const color = getCategoryColor(category);
            
            return React.createElement(
              'div',
              { key: category, className: 'mega-col' },
              React.createElement(
                'h4', 
                { className: 'mega-title', style: { color: color } },
                React.createElement('i', { className: `fas ${icon}`, style: { color: color } }),
                ' ',
                category
              ),
              items.map(item => {
                const isCustom = item.isCustom || false;
                
                return React.createElement(
                  'a',
                  {
                    key: item.id,
                    href: '#',
                    className: `mega-link ${isCustom ? 'custom-service-link' : ''}`,
                    style: isCustom ? {
                      color: '#8B5CF6',
                      fontWeight: '600',
                      borderLeft: '3px solid #8B5CF6',
                      paddingLeft: '8px'
                    } : {},
                    onClick: (e) => {
                      e.preventDefault();
                      handleNavigation(item.path || '/');
                    }
                  },
                  item.label,
                  isCustom && React.createElement(
                    'span',
                    { 
                      style: { 
                        fontSize: '10px', 
                        background: '#8B5CF6', 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: '12px',
                        marginLeft: '6px'
                      }
                    },
                    'Custom'
                  )
                );
              })
            );
          })
        )
      );
    }

    return React.createElement(
      'div',
      { 
        className: `mega-menu ${hoveredDropdown === 'career-services' ? 'show' : ''}`,
        ref: (el) => dropdownRefs.current['career-services'] = el
      },
      React.createElement(
        'div',
        { className: 'mega-grid' },
        headings.map(heading => {
          const items = groupedItems[heading] || [];
          const icon = getCategoryIcon(heading);
          const color = getCategoryColor(heading);
          
          return React.createElement(
            'div',
            { key: heading, className: 'mega-col' },
            React.createElement(
              'h4', 
              { className: 'mega-title', style: { color: color } },
              React.createElement('i', { className: `fas ${icon}`, style: { color: color } }),
              ' ',
              heading
            ),
            items.map(item => {
              const displayLabel = item.label.replace(/^ {2,}/, '');
              const isCustom = allServices[item.id] && allServices[item.id].isCustom;
              
              return React.createElement(
                'a',
                {
                  key: item.id,
                  href: '#',
                  className: `mega-link ${isCustom ? 'custom-service-link' : ''}`,
                  style: isCustom ? {
                    color: '#8B5CF6',
                    fontWeight: '600',
                    borderLeft: '3px solid #8B5CF6',
                    paddingLeft: '8px'
                  } : {},
                  onClick: (e) => {
                    e.preventDefault();
                    handleNavigation(item.path || '/');
                  }
                },
                displayLabel,
             
              );
            })
          );
        })
      )
    );
  };

  const renderSimpleDropdown = (item) => {
    const isExpanded = hoveredDropdown === item.id;
    const hasChildren = item.children && item.children.length > 0;

    return React.createElement(
      'li',
      { 
        key: item.id,
        className: `nav-item dropdown ${isExpanded ? 'active' : ''}`,
        onMouseEnter: () => handleMouseEnter(item.id),
        onMouseLeave: () => handleMouseLeave(item.id)
      },
      React.createElement(
        'span',
        { 
          className: 'nav-link dropdown-toggle',
          onClick: (e) => toggleDropdown(item.id, e)
        },
        React.createElement('i', { className: `fas ${getCategoryIcon(item.label)}`, style: { marginRight: '6px' } }),
        item.label,
        ' ',
        React.createElement('i', { className: `fas fa-chevron-${isExpanded ? 'up' : 'down'}` })
      ),
      React.createElement(
        'ul',
        { 
          className: `dropdown-menu ${isExpanded ? 'show' : ''}`,
          ref: (el) => dropdownRefs.current[item.id] = el
        },
        hasChildren ? item.children.map((child) => {
          if (child.type === 'divider') {
            return React.createElement('li', { key: child.id, className: 'dropdown-divider' });
          }
          const isCustom = child.isCustom || false;
          
          return React.createElement(
            'li',
            { key: child.id },
            React.createElement(
              'a',
              {
                href: '#',
                className: `dropdown-item ${isCustom ? 'custom-service-item' : ''}`,
                style: isCustom ? { color: '#8B5CF6', fontWeight: '500' } : {},
                onClick: (e) => {
                  e.preventDefault();
                  handleNavigation(child.path || '/');
                }
              },
              child.label,
              isCustom && React.createElement(
                'span',
                { 
                  style: { 
                    fontSize: '10px', 
                    background: '#8B5CF6', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px',
                    marginLeft: '8px'
                  }
                },
                'Custom'
              )
            )
          );
        }) : React.createElement(
          'li',
          null,
          React.createElement('span', { className: 'dropdown-empty' }, 'No items')
        )
      )
    );
  };

  const renderAccountDropdown = () => {
    const isExpanded = hoveredDropdown === 'account';
    const isLoggedIn = isUserLoggedIn();

    return React.createElement(
      'li',
      { 
        className: `nav-item dropdown account-dropdown ${isExpanded ? 'active' : ''}`,
        onMouseEnter: () => handleMouseEnter('account'),
        onMouseLeave: () => handleMouseLeave('account')
      },
      React.createElement(
        'span',
        { 
          className: 'nav-link dropdown-toggle',
          onClick: (e) => toggleDropdown('account', e)
        },
        React.createElement('i', { className: isLoggedIn ? 'fas fa-user-circle' : 'fas fa-user', style: { marginRight: '6px' } }),
        isLoggedIn ? (currentUser?.firstName || currentUser?.name || 'My Account') : 'Account',
        ' ',
        React.createElement('i', { className: `fas fa-chevron-${isExpanded ? 'up' : 'down'}` })
      ),
      React.createElement(
        'ul',
        { 
          className: `dropdown-menu account-dropdown-menu ${isExpanded ? 'show' : ''}`,
          ref: (el) => dropdownRefs.current['account'] = el,
          style: {
            position: 'absolute',
            top: '100%',
            right: '0',
            minWidth: '220px',
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            padding: '8px 0',
            zIndex: 1000,
            border: '1px solid #e2e8f0',
            opacity: isExpanded ? 1 : 0,
            visibility: isExpanded ? 'visible' : 'hidden',
            transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.25s ease'
          }
        },
        isLoggedIn ? [
          React.createElement(
            'li',
            { key: 'profile' },
            React.createElement(
              'a',
              {
                href: '#',
                className: 'dropdown-item',
                onClick: (e) => {
                  e.preventDefault();
                  handleNavigation('/profile');
                },
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 20px',
                  color: '#1e293b',
                  textDecoration: 'none',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500'
                }
              },
              React.createElement('i', { className: 'fas fa-id-card', style: { width: '20px', color: '#65d2bc' } }),
              ' My Profile'
            )
          ),
          React.createElement(
            'li',
            { key: 'cart' },
            React.createElement(
              'a',
              {
                href: '#',
                className: 'dropdown-item',
                onClick: (e) => {
                  e.preventDefault();
                  handleNavigation('/cart');
                },
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 20px',
                  color: '#1e293b',
                  textDecoration: 'none',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500'
                }
              },
              React.createElement('i', { className: 'fas fa-shopping-cart', style: { width: '20px', color: '#65d2bc' } }),
              ' My Cart',
              cartCount > 0 && React.createElement('span', { 
                className: 'dropdown-badge',
                style: {
                  background: '#65d2bc',
                  color: '#0f172a',
                  borderRadius: '50%',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  marginLeft: 'auto'
                }
              }, cartCount)
            )
          ),
          React.createElement('li', { key: 'divider', className: 'dropdown-divider', style: { borderTop: '1px solid #e2e8f0', margin: '4px 0' } }),
          React.createElement(
            'li',
            { key: 'logout' },
            React.createElement(
              'a',
              {
                href: '#',
                className: 'dropdown-item logout-item',
                onClick: (e) => {
                  e.preventDefault();
                  handleLogout();
                },
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 20px',
                  color: '#dc2626',
                  textDecoration: 'none',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500'
                }
              },
              React.createElement('i', { className: 'fas fa-sign-out-alt', style: { width: '20px' } }),
              ' Logout'
            )
          )
        ] : [
          React.createElement(
            'li',
            { key: 'login' },
            React.createElement(
              'a',
              {
                href: '#',
                className: 'dropdown-item',
                onClick: (e) => {
                  e.preventDefault();
                  handleNavigation('/login');
                },
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 20px',
                  color: '#1e293b',
                  textDecoration: 'none',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500'
                }
              },
              React.createElement('i', { className: 'fas fa-sign-in-alt', style: { width: '20px', color: '#65d2bc' } }),
              ' Login'
            )
          ),
          React.createElement(
            'li',
            { key: 'register' },
            React.createElement(
              'a',
              {
                href: '#',
                className: 'dropdown-item',
                onClick: (e) => {
                  e.preventDefault();
                  handleNavigation('/register');
                },
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 20px',
                  color: '#1e293b',
                  textDecoration: 'none',
                  transition: 'background 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500'
                }
              },
              React.createElement('i', { className: 'fas fa-user-plus', style: { width: '20px', color: '#65d2bc' } }),
              ' Register'
            )
          )
        ]
      )
    );
  };

  const renderNavigationFromFirebase = () => {
    if (headerLoading || navItems.length === 0) {
      return React.createElement(
        React.Fragment,
        null,
        React.createElement(
          'li',
          { className: 'nav-item' },
          React.createElement('span', { className: 'nav-link' }, 'Loading...')
        )
      );
    }

    const navElements = [];

    navItems.forEach((item) => {
      if (item.type === 'divider' || item.type === 'heading') {
        return;
      }

      if (item.id === 'home' || item.type === 'link') {
        navElements.push(
          React.createElement(
            'li',
            { key: item.id, className: 'nav-item' },
            React.createElement(
              'a',
              { 
                href: '#',
                className: 'nav-link',
                onClick: (e) => {
                  e.preventDefault();
                  handleNavigation(item.path || '/');
                }
              },
              item.label
            )
          )
        );
      } else if (item.id === 'career-services' || item.id === 'services') {
        const isExpanded = hoveredDropdown === 'career-services';
        navElements.push(
          React.createElement(
            'li',
            { 
              key: item.id,
              className: `nav-item dropdown mega ${isExpanded ? 'active' : ''}`,
              onMouseEnter: () => handleMouseEnter('career-services'),
              onMouseLeave: () => handleMouseLeave('career-services')
            },
            React.createElement(
              'span',
              { 
                className: 'nav-link dropdown-toggle',
                onClick: (e) => toggleDropdown('career-services', e)
              },
              React.createElement('i', { className: 'fas fa-cogs', style: { marginRight: '6px' } }),
              item.label || 'Career Services',
              ' ',
              React.createElement('i', { className: `fas fa-chevron-${isExpanded ? 'up' : 'down'}` })
            ),
            renderCareerServicesMegaMenu(item.children)
          )
        );
      } else if (item.type === 'dropdown') {
        navElements.push(renderSimpleDropdown(item));
      }
    });

    return navElements;
  };

  const renderLegacyMegaMenu = () => {
    const allServices = { ...services };
    customServices.forEach((service) => {
      if (service.id) {
        allServices[service.id] = {
          ...service,
          isCustom: true
        };
      }
    });

    if (loading || !allServices || Object.keys(allServices).length === 0) {
      return React.createElement(
        'div',
        { className: 'mega-menu' },
        React.createElement('p', null, 'Loading services...')
      );
    }

    const categories = new Set();
    Object.values(allServices).forEach(service => {
      if (service.category) categories.add(service.category);
    });

    return React.createElement(
      'div',
      { 
        className: `mega-menu ${hoveredDropdown === 'services' ? 'show' : ''}`,
        ref: (el) => dropdownRefs.current['services'] = el
      },
      React.createElement(
        'div',
        { className: 'mega-grid' },
        Array.from(categories).map(category => {
          const categoryServices = {};
          Object.entries(allServices).forEach(([id, service]) => {
            if (service.category === category) {
              categoryServices[id] = service;
            }
          });
          const icon = getCategoryIcon(category);
          const color = getCategoryColor(category);
          
          return React.createElement(
            'div',
            { key: category, className: 'mega-col' },
            React.createElement(
              'h4', 
              { className: 'mega-title', style: { color: color } },
              React.createElement('i', { className: `fas ${icon}`, style: { color: color } }),
              ' ',
              category
            ),
            Object.entries(categoryServices).map(([id, service]) => {
              const isCustom = service.isCustom || false;
              
              return React.createElement(
                'a',
                {
                  key: id,
                  href: '#',
                  className: `mega-link ${isCustom ? 'custom-service-link' : ''}`,
                  style: isCustom ? {
                    color: '#8B5CF6',
                    fontWeight: '600',
                    borderLeft: '3px solid #8B5CF6',
                    paddingLeft: '8px'
                  } : {},
                  onClick: (e) => {
                    e.preventDefault();
                    handleNavigation(`/service/${id}`);
                  }
                },
                service.name,
                isCustom && React.createElement(
                  'span',
                  { 
                    style: { 
                      fontSize: '10px', 
                      background: '#8B5CF6', 
                      color: 'white', 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      marginLeft: '6px'
                    }
                  },
                  'Custom'
                )
              );
            })
          );
        })
      )
    );
  };

  const isUserLoggedIn = () => {
    if (currentUser && currentUser.isLoggedIn === true) {
      return true;
    }
    try {
      const user = localStorage.getItem('currentUser');
      if (user) {
        const parsedUser = JSON.parse(user);
        if (parsedUser && parsedUser.isLoggedIn === true && parsedUser.userId) {
          if (!currentUser) {
            setCurrentUser(parsedUser);
            loadCartCount(parsedUser.userId);
          }
          return true;
        }
      }
    } catch (e) {
      console.error('Error checking login status:', e);
    }
    return false;
  };

  const hasFirebaseNav = navItems.length > 0 && !headerLoading;

  return React.createElement(
    'header',
    { className: `header ${scrolled ? 'scrolled' : ''}` },
    React.createElement(
      'div',
      { className: 'container' },
      React.createElement(
        'div',
        { className: 'header-inner' },
        React.createElement(
          'div',
          { className: 'logo' },
          React.createElement(
            Link,
            { to: '/', onClick: () => setMenuOpen(false) },
            React.createElement('img', {
              src: logo,
              alt: 'InfoNaukri',
              className: 'logo-img'
            })
          )
        ),
        React.createElement(
          'nav',
          { className: `main-nav ${menuOpen ? 'open' : ''}` },
          React.createElement(
            'ul',
            { className: 'nav-list' },
            hasFirebaseNav ? 
              renderNavigationFromFirebase() :
              React.createElement(
                React.Fragment,
                null,
                React.createElement(
                  'li',
                  { className: 'nav-item' },
                  React.createElement(
                    'a',
                    { 
                      href: '#',
                      className: 'nav-link',
                      onClick: (e) => {
                        e.preventDefault();
                        handleNavigation('/');
                      }
                    },
                    'Home'
                  )
                ),
                React.createElement(
                  'li',
                  { className: 'nav-item' },
                  React.createElement(
                    'a',
                    { 
                      href: '#',
                      className: 'nav-link',
                      onClick: (e) => {
                        e.preventDefault();
                        handleNavigation('/about');
                      }
                    },
                    'About Us'
                  )
                ),
                React.createElement(
                  'li',
                  { 
                    className: `nav-item dropdown mega ${hoveredDropdown === 'services' ? 'active' : ''}`,
                    onMouseEnter: () => handleMouseEnter('services'),
                    onMouseLeave: () => handleMouseLeave('services')
                  },
                  React.createElement(
                    'span',
                    { 
                      className: 'nav-link dropdown-toggle',
                      onClick: (e) => toggleDropdown('services', e)
                    },
                    'Career Services ',
                    React.createElement('i', { className: `fas fa-chevron-${hoveredDropdown === 'services' ? 'up' : 'down'}` })
                  ),
                  renderLegacyMegaMenu()
                )
              )
          )
        ),
        React.createElement(
          'div',
          { className: 'header-actions' },
          React.createElement(
            'button',
            { 
              className: `search-toggle ${searchOpen ? 'active' : ''}`, 
              onClick: () => setSearchOpen(!searchOpen),
              'aria-label': 'Toggle search'
            },
            React.createElement('i', { className: `fas ${searchOpen ? 'fa-times' : 'fa-search'}` })
          ),
          React.createElement(
            Link,
            { to: '/cart', className: 'cart-link', onClick: () => setMenuOpen(false) },
            cartCount > 0 && React.createElement('span', { className: 'cart-badge' }, cartCount),
            React.createElement('i', { className: 'fas fa-shopping-cart' })
          ),
          renderAccountDropdown(),
          React.createElement(
            'button',
            { 
              className: `menu-toggle ${menuOpen ? 'active' : ''}`, 
              onClick: () => setMenuOpen(!menuOpen),
              'aria-label': 'Toggle menu'
            },
            React.createElement('span', { className: 'hamburger' }),
            React.createElement('span', { className: 'hamburger' }),
            React.createElement('span', { className: 'hamburger' })
          )
        )
      )
    ),
    searchOpen && React.createElement(
      'div',
      { className: 'search-overlay' },
      React.createElement(
        'div',
        { className: 'container' },
        React.createElement(
          'div',
          { className: 'search-form' },
          React.createElement('i', { className: 'fas fa-search search-icon' }),
          React.createElement('input', {
            type: 'text',
            className: 'search-input',
            placeholder: 'Start typing & press Enter...',
            autoFocus: true
          }),
          React.createElement(
            'button',
            { 
              className: 'search-close',
              onClick: () => setSearchOpen(false)
            },
            React.createElement('i', { className: 'fas fa-times' })
          )
        )
      )
    ) 
  );
};

export default Header;